/** Product Types — CRUD шаблонов товаров с привязкой attributes (Phase B2). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Layers, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

interface Attr {
  id: number;
  slug: string;
  name: string;
  input_type: string;
}
interface ProductType {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  has_variants: boolean;
  attribute_ids: number[];
}

const EMPTY: Omit<ProductType, "id" | "attribute_ids"> & { attribute_ids: number[] } = {
  slug: "",
  name: "",
  description: null,
  has_variants: false,
  attribute_ids: [],
};

export function ProductTypesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | "new" | null>(null);

  const { data: types, isLoading } = useQuery({
    queryKey: ["product-types"],
    queryFn: async () => {
      const { data } = await api.get("/product-types");
      return data as ProductType[];
    },
  });

  const { data: attrs } = useQuery({
    queryKey: ["attributes"],
    queryFn: async () => {
      const { data } = await api.get("/attributes");
      return data as Attr[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/product-types/${id}`);
    },
    onSuccess: () => {
      toast.success("Тип удалён");
      queryClient.invalidateQueries({ queryKey: ["product-types"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось удалить"),
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить тип товара?",
  });

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration/attributes">
            Атрибуты →
          </Link>
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading flex items-center gap-2">
            <Layers className="w-6 h-6" />
            Product Types (шаблоны товаров)
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Каждый тип определяет набор атрибутов. Холодильник / Телевизор /
            Пылесос — каждый со своими характеристиками.
          </p>
        </div>
        <Button
          onClick={() => setEditingId("new")}
          disabled={!attrs}
        >
          <Plus className="w-4 h-4 mr-1" />
          Создать тип
        </Button>
      </div>

      {editingId === "new" && attrs && (
        <TypeForm
          mode="create"
          initial={EMPTY}
          attrs={attrs}
          onClose={() => setEditingId(null)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {types?.length === 0 && editingId !== "new" && (
          <Card className="lg:col-span-2">
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              Типов нет. Сначала создайте несколько{" "}
              <Link to="/configuration/attributes" className="text-primary underline">
                атрибутов
              </Link>
              , потом «Создать тип» и привяжите их.
            </CardContent>
          </Card>
        )}
        {types?.map((t) => (
          <Card key={t.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base">{t.name}</CardTitle>
                  <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                    {t.slug}
                  </div>
                </div>
                {t.has_variants && (
                  <Badge variant="secondary">с вариантами</Badge>
                )}
              </div>
              {t.description && (
                <p className="text-xs text-muted-foreground pt-1">
                  {t.description}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  Attributes ({t.attribute_ids.length})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {t.attribute_ids.length === 0 ? (
                    <span className="text-xs text-muted-foreground italic">
                      Не привязано
                    </span>
                  ) : (
                    t.attribute_ids.map((aid) => {
                      const a = attrs?.find((x) => x.id === aid);
                      return (
                        <Badge
                          key={aid}
                          variant="outline"
                          className="text-[10px]"
                        >
                          {a?.name ?? `#${aid}`}
                        </Badge>
                      );
                    })
                  )}
                </div>
              </div>

              {editingId === t.id && attrs ? (
                <TypeForm
                  mode="edit"
                  type={t}
                  initial={{
                    slug: t.slug,
                    name: t.name,
                    description: t.description,
                    has_variants: t.has_variants,
                    attribute_ids: t.attribute_ids,
                  }}
                  attrs={attrs}
                  onClose={() => setEditingId(null)}
                />
              ) : (
                <div className="flex items-center justify-between pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingId(t.id)}
                  >
                    Редактировать
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => askDelete(t.id, t.name)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      {deleteDialog}
    </div>
  );
}

function TypeForm({
  mode,
  type,
  initial,
  attrs,
  onClose,
}: {
  mode: "create" | "edit";
  type?: ProductType;
  initial: typeof EMPTY;
  attrs: Attr[];
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(initial);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        slug: form.slug.trim(),
        name: form.name.trim(),
        description: form.description?.trim() || null,
        has_variants: form.has_variants,
        attribute_ids: form.attribute_ids,
      };
      if (mode === "create") {
        await api.post("/product-types", payload);
      } else if (type) {
        await api.patch(`/product-types/${type.id}`, payload);
      }
    },
    onSuccess: () => {
      toast.success(mode === "create" ? "Создан" : "Сохранено");
      queryClient.invalidateQueries({ queryKey: ["product-types"] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка сохранения"),
  });

  function toggleAttr(id: number) {
    setForm((prev) => {
      const has = prev.attribute_ids.includes(id);
      return {
        ...prev,
        attribute_ids: has
          ? prev.attribute_ids.filter((x) => x !== id)
          : [...prev.attribute_ids, id],
      };
    });
  }

  return (
    <Card
      className={mode === "edit" ? "mt-3 border-primary/30" : undefined}
    >
      {mode === "create" && (
        <CardHeader>
          <CardTitle className="text-base">Новый тип</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-3 pt-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Slug *</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="sofa"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Название *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Холодильник"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Описание</Label>
          <Textarea
            value={form.description ?? ""}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
            rows={2}
          />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={form.has_variants}
            onChange={(e) =>
              setForm({
                ...form,
                has_variants: (e.target as HTMLInputElement).checked,
              })
            }
          />
          Имеет варианты (разные конфигурации одного товара)
        </label>

        <div>
          <Label className="mb-2 block">
            Привязанные атрибуты ({form.attribute_ids.length} / {attrs.length})
          </Label>
          <div className="border rounded-md max-h-60 overflow-y-auto divide-y">
            {attrs.length === 0 ? (
              <div className="text-center py-6 text-sm text-muted-foreground">
                Сначала создайте атрибуты на странице{" "}
                <Link
                  to="/configuration/attributes"
                  className="text-primary underline"
                >
                  «Атрибуты»
                </Link>
              </div>
            ) : (
              attrs.map((a) => (
                <label
                  key={a.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-muted/50 cursor-pointer"
                >
                  <Checkbox
                    checked={form.attribute_ids.includes(a.id)}
                    onChange={() => toggleAttr(a.id)}
                  />
                  <span className="flex-1 text-sm">{a.name}</span>
                  <span className="text-xs text-muted-foreground font-mono">
                    {a.slug} · {a.input_type}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={
              !form.slug.trim() || !form.name.trim() || saveMutation.isPending
            }
          >
            {mode === "create" ? "Создать" : "Сохранить"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
